import { useState } from 'react';
import { FEEDBACK_FLAGS, type FeedbackFlag, type MovieOption } from '../../shared/protocol';
import { BakingBar } from './BakingBar';

/**
 * "Review AI poster" modal — the finewood AiImagePanel loop, ported: a preview, the 14
 * quick-tag chips, a notes field, and Approve / Regenerate. The parent owns the API
 * calls (host token vs suggester code); this component just collects feedback.
 */

const FLAG_LABELS: Record<FeedbackFlag, string> = {
  'wrong-subject': 'wrong subject',
  'too-busy': 'too busy',
  'wrong-style': 'wrong style',
  'bad-composition': 'bad composition',
  'low-quality': 'low quality',
  'wrong-colors': 'wrong colors',
  'missing-parts': 'missing parts',
  'not-recognizable': 'not recognizable',
  'weird-text': 'weird text',
  'cropped-weirdly': 'cropped weirdly',
  'too-dark': 'too dark',
  'too-bright': 'too bright',
  creepy: 'creepy',
  'wrong-everything': 'wrong everything',
};

export function AiImageReview({
  option,
  busy,
  error,
  onApprove,
  onRegenerate,
  onClose,
}: {
  option: MovieOption;
  busy: boolean; // a bake is in flight (art === 'baking')
  error?: string | null;
  onApprove: () => void | Promise<void>;
  onRegenerate: (feedback: FeedbackFlag[], notes: string) => void | Promise<void>;
  onClose: () => void;
}) {
  const [flags, setFlags] = useState<FeedbackFlag[]>([]);
  const [notes, setNotes] = useState('');
  const [sending, setSending] = useState<null | 'approve' | 'regen'>(null);

  const toggle = (f: FeedbackFlag) =>
    setFlags((prev) => (prev.includes(f) ? prev.filter((x) => x !== f) : [...prev, f]));

  // regenBusy drives the instant "a new one is being made" feedback: the moment you
  // click, before the server round-trip flips art to 'baking'.
  const regenBusy = busy || sending === 'regen';
  const anyBusy = busy || sending !== null;

  const doApprove = async () => {
    setSending('approve');
    try {
      await onApprove();
    } finally {
      setSending(null);
    }
  };
  const doRegen = async () => {
    setSending('regen');
    try {
      await onRegenerate(flags, notes);
    } finally {
      setSending(null);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal card" onClick={(e) => e.stopPropagation()}>
        <div className="flex-between">
          <h2>🎨 Review AI poster</h2>
          <button className="remove-x" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>
        <p className="soft">{option.title}</p>

        <div className="review-preview">
          {option.posterUrl ? (
            <img
              src={option.posterUrl}
              alt={`${option.title} poster`}
              className={regenBusy ? 'baking' : ''}
            />
          ) : (
            <div className="review-placeholder">🎬</div>
          )}
        </div>

        {regenBusy && <BakingBar label="🎨 baking a new poster…" />}

        {/* the invented "movie facts" — the bit */}
        {(option.poster?.tagline || option.poster?.plot) && (
          <div className="poster-meta">
            {option.poster?.tagline && <div className="pm-tagline">“{option.poster.tagline}”</div>}
            {option.poster?.plot && <div className="pm-plot soft">{option.poster.plot}</div>}
            {(option.poster?.genre || option.poster?.year) && (
              <div className="pm-credit">
                {[option.poster?.genre, option.poster?.year].filter(Boolean).join(' · ')}
              </div>
            )}
          </div>
        )}

        <label>
          What&apos;s off? <span className="hint">(optional)</span>
        </label>
        <div className="chip-grid">
          {FEEDBACK_FLAGS.map((f) => (
            <button
              key={f}
              type="button"
              className={`chip toggle${flags.includes(f) ? ' on' : ''}`}
              onClick={() => toggle(f)}
            >
              {FLAG_LABELS[f]}
            </button>
          ))}
        </div>

        <label>
          Additional notes <span className="hint">(optional)</span>
        </label>
        <textarea
          maxLength={200}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="e.g. 'more neon', 'make it a snowy scene', 'less scary'…"
        />

        {error && <div className="notice error">{error}</div>}

        <div className="row" style={{ marginTop: 12 }}>
          <button className="btn green" onClick={doApprove} disabled={anyBusy}>
            {sending === 'approve' ? <span className="spinner" /> : '👍 Approve'}
          </button>
          <button className="btn" onClick={doRegen} disabled={anyBusy}>
            {regenBusy ? <span className="spinner" /> : '🔄 Regenerate'}
          </button>
        </div>
      </div>
    </div>
  );
}
